import { createFileRoute, Link } from "@tanstack/react-router";
import axios from "axios";
import { ptBR } from "date-fns/locale";
import { ArrowLeft, BookMarked, Info } from "lucide-react";
import { useEffect, useState } from "react";
import { QuestionReviewSheet } from "@/components/exam-result/question-review-sheet";
import {
	QuestionsOverview,
	type ResultQuestion,
} from "@/components/exam-result/questions-overview";
import { ResultIndicators } from "@/components/exam-result/result-indicators";
import { SentFilesTable } from "@/components/exam-result/sent-files-table";
import { TRIPerformance } from "@/components/exam-result/tri-performance";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useApplicationResult } from "@/hooks/queries/use-application-result";
import { useModal } from "@/hooks/use-modal";
import { formatGrade, parseLocalDateOnly, safeFormat } from "@/lib/utils";

export const Route = createFileRoute("/_app/painel/minhas-provas/$id")({
	component: ExamResultPage,
});

function ExamResultPage() {
	const { id } = Route.useParams();
	const { data, isLoading, error } = useApplicationResult(id);
	const { openModal } = useModal();

	// Escopo de navegação do painel: a lista visível no momento em que a questão
	// foi aberta, para que ‹ › respeitem o filtro e a ordenação escolhidos.
	const [navScope, setNavScope] = useState<ResultQuestion[]>([]);
	const [activeQuestionId, setActiveQuestionId] = useState<
		string | number | null
	>(null);

	const activeIndex = navScope.findIndex((q) => q.id === activeQuestionId);
	const activeQuestion = activeIndex >= 0 ? navScope[activeIndex] : null;
	const previousQuestion = activeIndex > 0 ? navScope[activeIndex - 1] : null;
	const nextQuestion =
		activeIndex >= 0 && activeIndex < navScope.length - 1
			? navScope[activeIndex + 1]
			: null;

	const openSubjectPerformance = (subject: {
		subjectId: string;
		subject: string;
	}) => {
		openModal("SubjectPerformanceModal", {
			applicationId: id,
			subjectId: subject.subjectId,
			subjectName: subject.subject,
		});
	};

	useEffect(() => {
		if (data?.application?.exam?.name) {
			document.title = data.application.exam.name;
		}
		return () => {
			document.title = "Lize";
		};
	}, [data]);

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-6 lg:max-w-6xl lg:mx-auto">
				<Skeleton className="h-4 w-32" />
				<div className="flex justify-between items-center">
					<Skeleton className="h-10 w-48" />
					<Skeleton className="h-20 w-80" />
				</div>
				<div className="grid grid-cols-3 gap-10">
					<Skeleton className="h-32 w-full" />
					<Skeleton className="h-32 w-full" />
					<Skeleton className="h-32 w-full" />
				</div>
			</div>
		);
	}

	if (error) {
		const status = axios.isAxiosError(error)
			? error.response?.status
			: undefined;

		const message =
			status === 401
				? "Você não tem permissão para visualizar este resultado."
				: "Ocorreu um erro ao carregar os resultados.";

		return (
			<div className="flex flex-col items-center justify-center h-[50vh] gap-4">
				<p className="text-slate-500 font-medium text-lg">{message}</p>
				<Button asChild variant="outline">
					<Link
						to="/painel/minhas-provas"
						search={{
							status: "disponiveis",
							page: 1,
							year: new Date().getFullYear().toString(),
						}}
					>
						Voltar para a lista
					</Link>
				</Button>
			</div>
		);
	}

	if (!data) {
		return (
			<div className="flex flex-col items-center justify-center h-[50vh] gap-4">
				<p className="text-slate-500 font-medium text-lg">
					Resultado não encontrado.
				</p>
				<Button asChild variant="outline">
					<Link
						to="/painel/minhas-provas"
						search={{
							status: "disponiveis",
							page: 1,
							year: new Date().getFullYear().toString(),
						}}
					>
						Voltar para a lista
					</Link>
				</Button>
			</div>
		);
	}

	const filesConcat = data.files
		? Object.entries(data.files).flatMap(([key, items]) =>
				items.map((item) => ({ ...item, category: key })),
			)
		: [];

	const exam = data.application?.exam;

	return (
		<div className="flex flex-col gap-2 py-6 px-4 lg:py-10 lg:max-w-6xl lg:mx-auto">
			<Link
				to="/painel/minhas-provas"
				search={{
					status: "disponiveis",
					page: 1,
					year: new Date().getFullYear().toString(),
				}}
				className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors w-fit"
			>
				<ArrowLeft size={16} />
				Minhas provas
			</Link>

			<div className="flex flex-col lg:flex-row justify-between items-start lg:items-center pt-6 gap-6">
				<div className="flex flex-col gap-1">
					<h1 className="text-3xl font-bold text-slate-900">Resultados</h1>
					<p className="text-slate-500 font-medium">
						Veja como foi seu desempenho
					</p>
				</div>
				<Card className="flex flex-row items-center gap-4 px-6 py-4 bg-white shadow-sm rounded-xl border-slate-100">
					<div className="size-12 shrink-0 rounded-full bg-slate-50 flex items-center justify-center">
						<BookMarked className="text-primary" size={24} />
					</div>
					<div className="flex min-w-0 flex-col gap-0.5">
						<h4 className="font-bold text-slate-900">
							{/* O payload do resultado não traz a categoria da aplicação, só
							    `{ id, date, exam? }` — sem como distinguir avaliação de lista
							    de exercício aqui. Fallback neutro em vez de chamar de
							    "Avaliação" algo que pode ser uma lista. */}
							{exam?.name ?? "Sem título"}
						</h4>
						<span className="text-sm font-semibold text-slate-400">
							{safeFormat(parseLocalDateOnly(data.application?.date), "PPP", {
								locale: ptBR,
							})}
						</span>
					</div>
				</Card>
			</div>

			<div className="h-px bg-slate-100 w-full mt-8" />

			{exam?.isEnemSimulator ? (
				<TRIPerformance examId={exam.id} questionsCount={data.questionsCount} />
			) : (
				<ResultIndicators
					questionsCount={data.questionsCount}
					performance={data.performance}
					score={data.score}
				/>
			)}

			<div className="space-y-8 pt-8">
				<QuestionsOverview
					questions={data.questionsData}
					onSelectQuestion={(question, visibleQuestions) => {
						setNavScope(visibleQuestions);
						setActiveQuestionId(question.id);
					}}
				/>

				<div className="flex flex-col gap-10 pt-10">
					{/* Disciplinas */}
					<div className="space-y-6">
						<h2 className="text-xl font-bold text-slate-900">Disciplinas</h2>
						<div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
							<Table>
								<TableHeader className="border-b border-slate-200 bg-white">
									<TableRow className="hover:bg-transparent border-none">
										<TableHead className="h-14 pl-6 pr-4 text-left text-xs font-semibold text-slate-500 min-w-[140px]">
											Disciplina
										</TableHead>
										<TableHead className="h-14 px-4 text-center text-xs font-semibold text-slate-500 whitespace-nowrap">
											Questões
										</TableHead>
										<TableHead className="h-14 px-4 text-center text-xs font-semibold text-slate-500 whitespace-nowrap">
											Acertos
										</TableHead>
										<TableHead className="h-14 px-4 text-center text-xs font-semibold text-slate-500 whitespace-nowrap">
											Parciais
										</TableHead>
										<TableHead className="h-14 px-4 text-center text-xs font-semibold text-slate-500 whitespace-nowrap">
											Erros
										</TableHead>
										<TableHead className="h-14 px-4 text-center text-xs font-semibold text-slate-500 whitespace-nowrap">
											Desempenho
										</TableHead>
										<TableHead className="h-14 pl-4 pr-6 text-center text-xs font-semibold text-slate-500 whitespace-nowrap">
											Nota
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.subjects.length === 0 ? (
										<TableRow className="border-b border-slate-100 hover:bg-transparent">
											<TableCell
												colSpan={7}
												className="h-24 text-center text-slate-500 font-medium"
											>
												Nenhuma disciplina nesta prova.
											</TableCell>
										</TableRow>
									) : (
										data.subjects.map((subject) => (
											<TableRow
												key={subject.subjectId}
												role="button"
												tabIndex={0}
												aria-label={`Ver desempenho em ${subject.subject}`}
												onClick={() => openSubjectPerformance(subject)}
												onKeyDown={(event) => {
													if (event.key === "Enter" || event.key === " ") {
														event.preventDefault();
														openSubjectPerformance(subject);
													}
												}}
												className="border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none"
											>
												<TableCell className="py-5 pl-6 pr-4 font-bold text-slate-900">
													{subject.subject}
												</TableCell>
												<TableCell className="py-5 px-4 text-center tabular-nums text-slate-700">
													{subject.examquestionsCount}
												</TableCell>
												<TableCell className="py-5 px-4 text-center tabular-nums text-slate-700">
													{subject.correctCount}
												</TableCell>
												<TableCell className="py-5 px-4 text-center tabular-nums text-slate-700">
													{subject.partialCount}
												</TableCell>
												<TableCell className="py-5 px-4 text-center tabular-nums text-slate-700">
													{subject.incorrectCount}
												</TableCell>
												<TableCell className="py-5 px-4 text-center font-bold tabular-nums text-slate-900">
													{subject.performance}
												</TableCell>
												<TableCell className="py-5 pl-4 pr-6 text-center font-bold tabular-nums text-slate-900 whitespace-nowrap">
													{formatGrade(subject.score)}
													<span className="text-slate-400 font-bold">
														/{formatGrade(subject.totalWeight)}
													</span>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</div>
					</div>

					{/* Arquivos */}
					<div className="space-y-6">
						<h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
							Arquivos enviados <Info size={18} className="text-slate-400" />
						</h2>
						<SentFilesTable files={filesConcat} />
					</div>
				</div>
			</div>

			<QuestionReviewSheet
				applicationId={id}
				open={activeQuestion !== null}
				onOpenChange={(open) => {
					if (!open) setActiveQuestionId(null);
				}}
				question={activeQuestion}
				previousQuestion={previousQuestion}
				nextQuestion={nextQuestion}
				onNavigate={(question) => setActiveQuestionId(question.id)}
			/>
		</div>
	);
}
